import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import type { ApiError, QboBatchRow } from '../api';
import { quickbooksEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars } from './event/shared';

/**
 * Finance → QuickBooks (D-138).
 *
 * The page answers three questions in the order somebody actually asks them:
 * are we connected, is everything mapped, and did last night's batches post.
 * A batch that failed says why on its own row, because "there is an error"
 * with the reason a click away is how an error gets ignored for a month.
 */

const tone = (status: string): Tone => (status === 'POSTED' ? 'ok' : status === 'ERROR' ? 'bad' : status === 'POSTING' ? 'warn' : status === 'SKIPPED' ? 'neutral' : 'accent');

export default function QuickBooksPage() {
  const can = usePermissions();
  if (!can('finance.reconciliation.view')) return <NotPermitted permission="finance.reconciliation.view" />;
  return <QuickBooks />;
}

function QuickBooks() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const overview = useQuery({ queryKey: ['qbo'], queryFn: quickbooksEndpoints.overview });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['qbo'] });
  const fail = (cause: ApiError) => setError(cause.message);

  const redirectUri = `${window.location.origin}/quickbooks`;

  const connect = useMutation({
    mutationFn: () => quickbooksEndpoints.connect(redirectUri),
    // Intuit's consent screen is a full page, not a popup we can reason about.
    onSuccess: (result) => window.location.assign(result.authorize_url),
    onError: fail,
  });

  const finish = useMutation({
    mutationFn: (input: { code: string; realm_id: string; state: string }) => quickbooksEndpoints.callback({ ...input, redirect_uri: redirectUri }),
    onSuccess: (result) => {
      setNotice(`Connected to QuickBooks company ${result.connection.realm_id}.`);
      refresh();
    },
    onError: fail,
  });

  // Intuit sends the customer back here with the code on the query string.
  useEffect(() => {
    const code = params.get('code');
    const realmId = params.get('realmId');
    const state = params.get('state');
    if (!code || !realmId || !state || finish.isPending) return;
    setParams({}, { replace: true });
    finish.mutate({ code, realm_id: realmId, state });
  }, [params, setParams, finish]);

  const disconnect = useMutation({ mutationFn: quickbooksEndpoints.disconnect, onSuccess: () => { setNotice('Disconnected.'); refresh(); }, onError: fail });
  const build = useMutation({
    mutationFn: () => quickbooksEndpoints.build(),
    onSuccess: (result) => {
      const bad = result.built.filter((b) => b.missing_mappings.length > 0);
      setNotice(`Built ${result.built.length} batch${result.built.length === 1 ? '' : 'es'}.${bad.length > 0 ? ` ${bad.length} need a mapping first.` : ''}`);
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['qbo-batches'] });
    },
    onError: fail,
  });
  const verify = useMutation({
    mutationFn: quickbooksEndpoints.verify,
    onSuccess: (result) => setNotice(result.drifted.length === 0 ? `Checked ${result.checked} posted batches; all still match.` : `${result.drifted.length} changed in QuickBooks: ${result.drifted.join('; ')}`),
    onError: fail,
  });

  const data = overview.data;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}

      <Panel
        title="QuickBooks Online"
        description="Ticketing owns the orders, payments and ledger; QuickBooks is a destination. Each batch is one balanced JournalEntry built from journal entries that have already been written, so a failure here can never change what a sale did."
        actions={
          can('finance.qbo.manage') && data ? (
            data.connection ? (
              <Button onClick={() => { if (window.confirm('Disconnect QuickBooks? Batches stop posting until it is reconnected.')) disconnect.mutate(); }} disabled={disconnect.isPending}>
                Disconnect
              </Button>
            ) : (
              <Button variant="primary" onClick={() => connect.mutate()} disabled={!data.configured || connect.isPending}>
                {connect.isPending ? 'Opening Intuit…' : 'Connect'}
              </Button>
            )
          ) : null
        }
      >
        {!data ? (
          <p className="text-sm text-ink-500">Loading…</p>
        ) : !data.configured ? (
          <p className="text-sm text-bad">
            QBO_CLIENT_ID, QBO_CLIENT_SECRET and QBO_TOKEN_ENCRYPTION_KEY are not set on this environment. Until they are, nothing can connect.
          </p>
        ) : (
          <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <Fact label="Company">{data.connection ? data.connection.realm_id : 'Not connected'}</Fact>
            <Fact label="Environment">{data.environment === 'PRODUCTION' ? 'Production company' : 'Sandbox company'}</Fact>
            <Fact label="Connection">{data.connection ? <Badge tone={data.connection.status === 'ACTIVE' ? 'ok' : 'bad'}>{data.connection.status}</Badge> : '—'}</Fact>
            <Fact label="Connected">{data.connection ? formatWhen(data.connection.connected_at) : '—'}</Fact>
            <Fact label="Mappings">
              {data.ready_to_post ? <Badge tone="ok">Every code mapped</Badge> : <Badge tone="warn">{data.unmapped.length} code{data.unmapped.length === 1 ? '' : 's'} unmapped</Badge>}
            </Fact>
            <Fact label="Journals not yet batched">{data.unbatched_journals.toLocaleString()}</Fact>
          </dl>
        )}
      </Panel>

      {data && data.unmapped.length > 0 ? (
        <Panel
          title="Accounting codes without an account"
          description="Nothing posts while a code in a batch has no QuickBooks account. There is no default and no suspense account on purpose: a batch says which code is missing and waits, rather than putting money somewhere plausible."
        >
          <MappingEditor codes={data.unmapped} onSaved={(message) => { setNotice(message); refresh(); }} onError={fail} />
        </Panel>
      ) : null}

      {data && data.mappings.length > 0 ? (
        <Panel title="Mapped accounts" description="Changing one supersedes the old row rather than editing it: a batch posted in March was posted against what the mapping said in March, and the books have to stay explicable. Anything not yet posted is rebuilt against the new account.">
          <Table headers={['Code', 'Family', 'QuickBooks account', 'Since', '']}>
            {data.mappings.map((row) => (
              <MappedRow key={row.id} row={row} canChange={can('finance.accounting_mappings.manage')} onSaved={(message) => { setNotice(message); refresh(); }} onError={fail} />
            ))}
          </Table>
        </Panel>
      ) : null}

      <BatchList
        canManage={can('finance.reconciliation.manage')}
        onBuild={() => build.mutate()}
        building={build.isPending}
        onVerify={() => verify.mutate()}
        verifying={verify.isPending}
        onError={fail}
        onNotice={setNotice}
      />
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1.5">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

/**
 * One mapped code, and the way to change it (D-333). The row said "changing one
 * supersedes the old row" and offered nothing to change it with.
 */
function MappedRow({ row, canChange, onSaved, onError }: { row: { accounting_code: string; code_name: string; family: string; qbo_account_id: string; qbo_account_name_snapshot: string; effective_from: string }; canChange: boolean; onSaved: (message: string) => void; onError: (error: ApiError) => void }) {
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState(row.qbo_account_id);
  const accounts = useQuery({ queryKey: ['qbo-accounts'], queryFn: quickbooksEndpoints.accounts, retry: false, enabled: editing });
  const save = useMutation({
    mutationFn: () => quickbooksEndpoints.saveMapping({ accounting_code: row.accounting_code, qbo_account_id: chosen }),
    onSuccess: (result) => {
      setEditing(false);
      onSaved(`${row.code_name} now posts to the new account.${result.rebuilt ? ` ${result.rebuilt} unposted batch${result.rebuilt === 1 ? '' : 'es'} rebuilt.` : ''}${result.type_warning ? ` ${result.type_warning}` : ''}`);
    },
    onError,
  });
  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2 font-medium">{row.code_name}</td>
      <td className="px-3 py-2 text-ink-500">{row.family.toLowerCase().replace('_', '-')}</td>
      <td className="px-3 py-2">
        {editing ? (
          accounts.isError ? (
            <span className="text-sm text-ink-500">Connect QuickBooks to choose accounts.</span>
          ) : (
            <select className={INPUT} value={chosen} onChange={(e) => setChosen(e.target.value)} aria-label={`QuickBooks account for ${row.code_name}`}>
              {!(accounts.data?.accounts ?? []).some((a) => a.Id === row.qbo_account_id) ? <option value={row.qbo_account_id}>{row.qbo_account_name_snapshot}</option> : null}
              {(accounts.data?.accounts ?? []).map((account) => (
                <option key={account.Id} value={account.Id}>
                  {account.Name} ({account.AccountType})
                </option>
              ))}
            </select>
          )
        ) : (
          row.qbo_account_name_snapshot
        )}
      </td>
      <td className="px-3 py-2 text-ink-500">{formatWhen(row.effective_from)}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {canChange && !editing ? <Button onClick={() => setEditing(true)}>Change</Button> : null}
        {editing ? (
          <span className="inline-flex gap-2">
            <Button variant="primary" disabled={save.isPending || chosen === row.qbo_account_id} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button onClick={() => { setEditing(false); setChosen(row.qbo_account_id); }}>Cancel</Button>
          </span>
        ) : null}
      </td>
    </tr>
  );
}

function MappingEditor({ codes, onSaved, onError }: { codes: { code: string; name: string; family: string }[]; onSaved: (message: string) => void; onError: (error: ApiError) => void }) {
  const accounts = useQuery({ queryKey: ['qbo-accounts'], queryFn: quickbooksEndpoints.accounts, retry: false });
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (input: { accounting_code: string; qbo_account_id: string }) => quickbooksEndpoints.saveMapping(input),
    onSuccess: (result, input) => onSaved(result.type_warning ? `Mapped ${input.accounting_code}. ${result.type_warning}` : `Mapped ${input.accounting_code}.`),
    onError,
  });

  if (accounts.isError) return <p className="text-sm text-ink-500">Connect QuickBooks to choose accounts.</p>;

  return (
    <Table headers={['Code', 'Family', 'QuickBooks account', '']}>
      {codes.map((code) => (
        <tr key={code.code} className="border-t border-line">
          <td className="px-3 py-2 font-medium">{code.name}</td>
          <td className="px-3 py-2 text-ink-500">{code.family.toLowerCase().replace('_', '-')}</td>
          <td className="px-3 py-2">
            <select className={INPUT} value={chosen[code.code] ?? ''} onChange={(e) => setChosen((prior) => ({ ...prior, [code.code]: e.target.value }))} aria-label={`QuickBooks account for ${code.name}`}>
              <option value="">— choose an account —</option>
              {(accounts.data?.accounts ?? []).map((account) => (
                <option key={account.Id} value={account.Id}>
                  {account.Name} ({account.AccountType})
                </option>
              ))}
            </select>
          </td>
          <td className="px-3 py-2 text-right">
            <Button disabled={!chosen[code.code] || save.isPending} onClick={() => save.mutate({ accounting_code: code.code, qbo_account_id: chosen[code.code]! })}>
              Save
            </Button>
          </td>
        </tr>
      ))}
    </Table>
  );
}

function BatchList({
  canManage,
  onBuild,
  building,
  onVerify,
  verifying,
  onError,
  onNotice,
}: {
  canManage: boolean;
  onBuild: () => void;
  building: boolean;
  onVerify: () => void;
  verifying: boolean;
  onError: (error: ApiError) => void;
  onNotice: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const batches = useQuery({ queryKey: ['qbo-batches', status], queryFn: () => quickbooksEndpoints.batches(status || undefined) });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['qbo-batches'] });
    void queryClient.invalidateQueries({ queryKey: ['qbo'] });
  };

  const post = useMutation({
    mutationFn: (id: string) => quickbooksEndpoints.post(id),
    onSuccess: (result) => {
      onNotice(result.status === 'POSTED' ? (result.adopted ? 'That entry was already in QuickBooks; the batch now points at it.' : 'Posted.') : `Not posted: ${result.error ?? result.status}`);
      refresh();
    },
    onError,
  });
  const rebuild = useMutation({
    mutationFn: (id: string) => quickbooksEndpoints.rebuild(id),
    onSuccess: (result) => { onNotice(result.missing_mappings.length > 0 ? `Still missing: ${result.missing_mappings.join(', ')}` : `Rebuilt ${result.lines} lines.`); refresh(); },
    onError,
  });
  const skip = useMutation({
    mutationFn: (input: { id: string; reason: string }) => quickbooksEndpoints.skip(input.id, input.reason),
    onSuccess: () => { onNotice('Skipped. Its journal entries are available to a new batch.'); refresh(); },
    onError,
  });

  return (
    <Panel
      title="Batches"
      description="Built at 04:00 for the previous day, and posted every ten minutes. A retry reuses the same Intuit request id and looks the document number up in QuickBooks first, so a lost response can never become a second journal entry."
      actions={
        canManage ? (
          <span className="flex items-center gap-2">
            <select className={INPUT} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
              <option value="">All statuses</option>
              {['READY', 'POSTED', 'ERROR', 'SKIPPED'].map((value) => (
                <option key={value} value={value}>
                  {value.toLowerCase()}
                </option>
              ))}
            </select>
            <Button onClick={onVerify} disabled={verifying}>{verifying ? 'Checking…' : 'Check posted entries'}</Button>
            <Button variant="primary" onClick={onBuild} disabled={building}>{building ? 'Building…' : 'Build now'}</Button>
          </span>
        ) : null
      }
    >
      <Table headers={['Batch', 'Date', 'Journals', 'Amount', 'Status', 'QuickBooks', '']}>
        {(batches.data?.batches ?? []).length === 0 ? (
          <EmptyRow columns={7}>Nothing batched yet.</EmptyRow>
        ) : (
          (batches.data?.batches ?? []).map((batch: QboBatchRow) => (
            <tr key={batch.id} className="border-t border-line align-top">
              <td className="px-3 py-2">
                <span className="font-medium">{batch.batch_number}</span>
                <span className="block text-xs text-ink-500">{batch.qbo_doc_number}</span>
              </td>
              <td className="px-3 py-2 tabular">{batch.accounting_date}</td>
              <td className="px-3 py-2 tabular">{batch.journals}</td>
              <td className="px-3 py-2 tabular">{dollars(batch.total_debits_cents)}</td>
              <td className="px-3 py-2">
                <Badge tone={tone(batch.status)}>{batch.status.toLowerCase()}</Badge>
                {/* The reason is on the row, not a click away: an error nobody
                    can see without opening it is an error nobody opens. */}
                {batch.last_error ? <span className="mt-1 block max-w-md text-xs break-words text-bad">{batch.last_error}</span> : null}
              </td>
              <td className="px-3 py-2 text-xs text-ink-500">
                {batch.qbo_transaction_id ? `#${batch.qbo_transaction_id}` : '—'}
                {batch.posted_at ? <span className="block">{formatWhen(batch.posted_at)}</span> : null}
                {batch.attempts > 1 ? <span className="block">{batch.attempts} attempts</span> : null}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {canManage && batch.status !== 'POSTED' && batch.status !== 'SKIPPED' ? (
                  <>
                    <Button onClick={() => post.mutate(batch.id)} disabled={post.isPending}>Post</Button>
                    <Button className="ml-2" onClick={() => rebuild.mutate(batch.id)} disabled={rebuild.isPending}>Rebuild</Button>
                    <Button
                      className="ml-2"
                      onClick={() => {
                        const reason = window.prompt('Why is this batch being skipped? Its journal entries go back in the queue.');
                        if (reason?.trim()) skip.mutate({ id: batch.id, reason: reason.trim() });
                      }}
                      disabled={skip.isPending}
                    >
                      Skip
                    </Button>
                  </>
                ) : null}
              </td>
            </tr>
          ))
        )}
      </Table>
    </Panel>
  );
}
